import { getGlobalData } from "utils/api";
import { getLocalizedPaths } from "utils/localize";
import Image from "next/image";

import Breadcrumb from "@/components/brandRefresh/common/Breadcrumb";
import { RefreshHeader } from "@/components/brandRefresh/common/PageTemplate";

export default function Accessibility({ metadata }) {
  return (
    <RefreshHeader metadata={metadata}>
      <div className="flex flex-col gap-spacing-14 text-sm md:text-base 4xl:text-xl">
        <Breadcrumb />
        <div className="p-10 bg-onmoPurple-50  text-onmoText rounded-xl text-center">
          <p className="text-base md:text-xl mb-4 font-medium">
            Common Scams and how to prevent fraudsters from winning
          </p>
          <p>
            Your online safety is crucial. Below, we outline key practices and
            safeguards to protect you from phishing, smishing, vishing, identity
            theft, and enhance your overall online security.
          </p>
        </div>

        <div className="flex gap-4 items-start flex-col md:flex-row lg:flex-row">
          <Image
            src="/images/icons/email.svg"
            alt="email-icon"
            className="p-1 h-8 w-8"
            height="32"
            width="32"
          />
          <div>
            <h3 className="text-onmoText text-2xl 4xl:text-3xl font-medium mb-2">
              What is Phishing?
            </h3>
            <p>
              Phishing involves deceptive emails or messages designed to trick
              you into sharing sensitive information. Smishing and vishing are
              similar scams using SMS/text messages and voice calls,
              respectively.
            </p>
            <p className="mt-6">To stay safe:</p>
            <div className="mt-6">
              <ul className="list-disc pl-6 flex flex-col gap-5">
                <li>
                  Verify Email, Text, and Call Sources: Ensure that emails, text
                  messages, and calls are legitimate before clicking any links,
                  opening attachments, or sharing information.
                </li>
                <li>
                  Beware of Urgent or Suspicious Requests: Fraudsters often
                  create a sense of urgency to push you into quick action.
                </li>
                <li>
                  Never Share Personal Information: Don? t disclose passwords,
                  banking details, or personal information in response to
                  unsolicited communication.
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex gap-4 items-start flex-col md:flex-row lg:flex-row">
          <Image
            src="/images/icons/comment.svg"
            alt="comment-icon"
            className="p-1 h-8 w-8"
            height="32"
            width="32"
          />
          <div>
            <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
              What is Smishing?
            </h3>
            <p>
              Smishing is a type of phishing scam conducted via SMS or text
              message. These messages may appear to come from trusted sources
              like banks or government agencies, asking you to click on a link
              or provide information. Tip: Avoid clicking on links in unexpected
              or suspicious texts.
            </p>
          </div>
        </div>
        <div className="flex gap-4 items-start flex-col md:flex-row lg:flex-row">
          <Image
            src="/images/icons/headphone.svg"
            alt="headphone-icon"
            className="p-1 h-8 w-8"
            height="32"
            width="32"
          />
          <div>
            <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
              What is Vishing?
            </h3>
            <p>
              Vishing uses phone calls to deceive individuals into sharing
              confidential information. Scammers may pose as bank
              representatives or tech support to gain access to your details.
              Tip: Hang up and call back using a verified number if you? re
              unsure of a call? s legitimacy.
            </p>
          </div>
        </div>

        <div className="flex gap-4 items-start flex-col md:flex-row lg:flex-row">
          <Image
            src="/images/icons/fingerprint.svg"
            alt="email-icon"
            className="p-1 h-8 w-8"
            height="32"
            width="32"
          />
          <div>
            <h3 className="text-onmoText text-2xl 4xl:text-3xl font-medium mb-2">
              Preventing Identity Theft
            </h3>
            <p>
              Identity theft can have severe financial and personal
              consequences. Protect yourself by:
            </p>
            <div className="mt-6">
              <ul className="list-disc pl-6">
                <li>
                  Secure Your Personal Data: Keep personal information private
                  and only share it on trusted, encrypted websites (look for ?
                  https://? in the URL).
                </li>
                <li>
                  Monitor Your Accounts: Regularly check bank statements and
                  credit reports for any unauthorised activity.
                </li>
                <li>
                  Strong, Unique Passwords: Use complex passwords and update
                  them regularly. Enable two-factor authentication where
                  available.
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div className="flex gap-4 items-start flex-col md:flex-row lg:flex-row">
          <Image
            src="/images/icons/safe-guard.svg"
            alt="email-icon"
            className="p-1 h-8 w-8"
            height="32"
            width="32"
          />
          <div>
            <h3 className="text-onmoText text-2xl 4xl:text-3xl font-medium mb-2">
              General Online Security Tips
            </h3>
            <p>
              Adopting these practices will help safeguard your online presence:
            </p>
            <div className="mt-6">
              <ul className="list-disc pl-6">
                <li>
                  Keep Software Up to Date: Regular updates for your devices and
                  apps include essential security patches.
                </li>
                <li>
                  Use Reliable Security Software: Install and maintain reputable
                  antivirus and anti-malware tools.
                </li>
                <li>
                  Be Cautious with Public Wi-Fi: Avoid accessing sensitive
                  accounts or sharing personal information on public Wi-Fi
                  without a secure VPN.
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div className="flex gap-4 items-start flex-col md:flex-row lg:flex-row">
          <Image
            src="/images/icons/more-info.svg"
            alt="eye-icon"
            className="p-1 h-8 w-8"
            height="32"
            width="32"
          />
          <div>
            <h3 className="text-onmoText text-xl md:text-2xl 4xl:text-3xl font-medium mb-2">
              What to Do if You? re a Victim
            </h3>
            <p>
              If you suspect phishing, smishing, vishing, identity theft, or
              have experienced a security breach:
            </p>
            <p className="mt-6">
              Report it Immediately: Contact your bank, relevant financial
              institutions, and, if needed, local authorities. Change Affected
              Passwords: Update your login details across all impacted accounts.
              Monitor for Suspicious Activity: Keep an eye on your accounts for
              any unauthorized transactions or activities.
            </p>
          </div>
        </div>
        <div className="p-10 bg-onmoPurple-50  text-onmoText rounded-xl text-center">
          <p className="text-base md:text-xl font-medium">
            Stay Informed: Awareness is your first line of defense.
          </p>
        </div>
      </div>
    </RefreshHeader>
  );
}

export async function getServerSideProps(context) {
  const { locale, locales, defaultLocale } = context;
  const globalLocale = await getGlobalData(locale);

  const pageContext = {
    locale,
    locales,
    defaultLocale,
    slug: context.resolvedUrl.replace(/\//g, ""),
  };

  const localizedPaths = getLocalizedPaths(pageContext);

  const metadata = {
    metaTitle: `How to keep yourself safe`,
  };

  return {
    props: {
      metadata,
      global: globalLocale.data,
      pageContext: {
        ...pageContext,
        localizedPaths,
      },
    },
  };
}
